import type { CVDesign } from '~/constants/cvDesign'
import type { PdfTemplateId } from '~/constants/cvTemplates'
import type { CVData } from '~/types/cv'

/**
 * The resume examples: /resume-examples/<slug>, one page per profession or
 * life situation, plus the hub at /resume-examples.
 *
 * This list is the index: the hub, the sitemap, related links, the route's 404
 * check, the server's sample renderer and "Use this example" all read it. The
 * content of each page lives in data/resume-examples/<slug>.json (one file per
 * page, loaded only by its own page), in Czech in data/resume-examples/cs, for
 * some pages in German in data/resume-examples/de (those with a `roleDe`), and
 * tests/constants/resumeExamples.test.ts keeps them in step.
 *
 * Picked by US search volume for "<role> resume examples" (DataForSEO, checked
 * 2026-09-29; see doc/seo/brief-resume-examples.md). These pages own the
 * profession queries, so the blog generator no longer writes them
 * (server/utils/blogTopics.ts) and the older articles redirect here
 * (constants/blogRedirects.ts).
 */
export type ResumeExampleGroup =
  | 'education'
  | 'healthcare'
  | 'business'
  | 'technology'
  | 'service'
  | 'trades'
  | 'situation'

/**
 * Accent colour each group's samples are shown in (a CV_ACCENT_PRESETS hex),
 * so the examples also show that a layout comes in more than one colour.
 * "Use this example" starts the new CV in the same colour.
 */
export const RESUME_EXAMPLE_GROUP_ACCENTS: Record<ResumeExampleGroup, string> = {
  education: '#15803d',
  healthcare: '#0f766e',
  business: '#1e3a8a',
  technology: '#1d4ed8',
  service: '#c2410c',
  trades: '#374151',
  situation: '#6d28d9',
}

/** The design a sample CV is shown and started in. */
export function exampleDesign(item: Pick<ResumeExampleIndexItem, 'group'>): CVDesign {
  return { accentColor: RESUME_EXAMPLE_GROUP_ACCENTS[item.group] }
}

/** Hub order. A group with no page yet is simply not shown. */
export const RESUME_EXAMPLE_GROUPS: ResumeExampleGroup[] = [
  'education',
  'healthcare',
  'business',
  'technology',
  'service',
  'trades',
  'situation',
]

export interface ResumeExampleIndexItem {
  slug: string
  /** Display form in headings and links: "Teacher", "Registered Nurse". */
  role: string
  /** How the role reads mid-sentence, when lower case would be wrong ("CNA", "HR"). */
  phrase?: string
  /** The role on the Czech page (data/resume-examples/cs), as its heading shows it. */
  roleCs: string
  /**
   * The role on the German page (data/resume-examples/de), for the pages that
   * have German content. Without it, /de shows the English page, kept out of
   * the index (constants/appLocale.ts, hasOwnContent).
   */
  roleDe?: string
  /**
   * A profession gets summaries for three levels of experience; a situation
   * (student, career change) gets variants of that situation instead.
   */
  kind: 'profession' | 'situation'
  group: ResumeExampleGroup
  /** US monthly searches for "<role> resume examples", for ordering the hub. */
  volume: number
  /** The layout the sample is shown on first, and the one the page recommends. */
  template: PdfTemplateId
  /** The SERP titles carry the year, so ours does too (brief, section 5). */
  yearInTitle: boolean
}

export const RESUME_EXAMPLES: ResumeExampleIndexItem[] = [
  { slug: 'teacher', role: 'Teacher', roleCs: 'Učitel', kind: 'profession', group: 'education', volume: 4400, template: 'ats', yearInTitle: true },
  // Also answers "nurse resume examples" (1,900) and "rn resume examples" (2,400).
  { slug: 'registered-nurse', role: 'Registered Nurse', roleCs: 'Zdravotní sestra', kind: 'profession', group: 'healthcare', volume: 2400, template: 'basic', yearInTitle: true },
  { slug: 'student', role: 'Student', roleCs: 'Student', kind: 'situation', group: 'situation', volume: 4400, template: 'basic', yearInTitle: true },
  { slug: 'special-education-teacher', role: 'Special Education Teacher', roleCs: 'Speciální pedagog', kind: 'profession', group: 'education', volume: 480, template: 'ats', yearInTitle: false },
  { slug: 'cna', role: 'CNA', roleCs: 'Ošetřovatel', phrase: 'CNA', kind: 'profession', group: 'healthcare', volume: 2900, template: 'basic', yearInTitle: true },
  { slug: 'medical-assistant', role: 'Medical Assistant', roleCs: 'Zdravotnický asistent', kind: 'profession', group: 'healthcare', volume: 1900, template: 'ats', yearInTitle: true },
  { slug: 'dental-assistant', role: 'Dental Assistant', roleCs: 'Zubní asistentka', kind: 'profession', group: 'healthcare', volume: 1000, template: 'modern', yearInTitle: true },
  { slug: 'customer-service', role: 'Customer Service', roleCs: 'Zákaznická podpora', kind: 'profession', group: 'service', volume: 2400, template: 'basic', yearInTitle: true },
  { slug: 'server', role: 'Server', roleCs: 'Číšník', kind: 'profession', group: 'service', volume: 1900, template: 'modern', yearInTitle: true },
  { slug: 'bartender', role: 'Bartender', roleCs: 'Barman', kind: 'profession', group: 'service', volume: 1300, template: 'ats', yearInTitle: true },
  { slug: 'retail', role: 'Retail', roleCs: 'Prodavač', kind: 'profession', group: 'service', volume: 1300, template: 'basic', yearInTitle: true },
  { slug: 'project-manager', role: 'Project Manager', roleCs: 'Projektový manažer', roleDe: 'Projektmanager', kind: 'profession', group: 'business', volume: 1300, template: 'classic', yearInTitle: true },
  { slug: 'sales', role: 'Sales', roleCs: 'Obchodník', kind: 'profession', group: 'business', volume: 1300, template: 'modern', yearInTitle: true },
  { slug: 'marketing', role: 'Marketing', roleCs: 'Marketing', roleDe: 'Marketing', kind: 'profession', group: 'business', volume: 1000, template: 'ats', yearInTitle: true },
  { slug: 'human-resources', role: 'Human Resources', roleCs: 'Personalista', roleDe: 'Personalwesen', kind: 'profession', group: 'business', volume: 1300, template: 'basic', yearInTitle: true },
  { slug: 'administrative-assistant', role: 'Administrative Assistant', roleCs: 'Administrativní asistent', roleDe: 'Verwaltungsassistent', kind: 'profession', group: 'business', volume: 1000, template: 'modern', yearInTitle: true },
  { slug: 'accountant', role: 'Accountant', roleCs: 'Účetní', roleDe: 'Buchhalter', kind: 'profession', group: 'business', volume: 480, template: 'classic', yearInTitle: true },
  { slug: 'software-engineer', role: 'Software Engineer', roleCs: 'Softwarový inženýr', roleDe: 'Softwareentwickler', kind: 'profession', group: 'technology', volume: 1000, template: 'basic', yearInTitle: true },
  { slug: 'data-analyst', role: 'Data Analyst', roleCs: 'Datový analytik', roleDe: 'Datenanalyst', kind: 'profession', group: 'technology', volume: 1000, template: 'ats', yearInTitle: true },
  { slug: 'internship', role: 'Internship', roleCs: 'Stáž', kind: 'situation', group: 'situation', volume: 1300, template: 'ats', yearInTitle: false },
  { slug: 'entry-level', role: 'Entry-Level', roleCs: 'Bez praxe', kind: 'situation', group: 'situation', volume: 1000, template: 'basic', yearInTitle: true },
  { slug: 'pharmacy-technician', role: 'Pharmacy Technician', roleCs: 'Farmaceutický asistent', kind: 'profession', group: 'healthcare', volume: 880, template: 'basic', yearInTitle: true },
  { slug: 'caregiver', role: 'Caregiver', roleCs: 'Pečovatel', kind: 'profession', group: 'healthcare', volume: 1000, template: 'modern', yearInTitle: true },
  { slug: 'electrician', role: 'Electrician', roleCs: 'Elektrikář', roleDe: 'Elektriker', kind: 'profession', group: 'trades', volume: 880, template: 'ats', yearInTitle: true },
  { slug: 'truck-driver', role: 'Truck Driver', roleCs: 'Řidič kamionu', roleDe: 'Lkw-Fahrer', kind: 'profession', group: 'trades', volume: 880, template: 'basic', yearInTitle: true },
  { slug: 'career-change', role: 'Career Change', roleCs: 'Změna oboru', roleDe: 'Berufswechsel', kind: 'situation', group: 'situation', volume: 260, template: 'modern', yearInTitle: true },
]

export const RESUME_EXAMPLE_SLUGS = RESUME_EXAMPLES.map(item => item.slug)

/** The pages with German content, data/resume-examples/de. */
export const RESUME_EXAMPLE_SLUGS_DE = RESUME_EXAMPLES.filter(item => item.roleDe).map(item => item.slug)

/**
 * Which translation of a page's content a language shows: 'cs' and 'de' read
 * data/resume-examples/<lang>, null the English original. German falls back to
 * English for the pages not translated yet.
 */
export function exampleContentLang(item: ResumeExampleIndexItem, locale: string): 'cs' | 'de' | null {
  if (locale === 'cs') return 'cs'
  if (locale === 'de' && item.roleDe) return 'de'
  return null
}

/**
 * The rest of the first wave (doc/seo/brief-resume-examples.md), not built yet.
 * An entry may already name these as related; the page shows only related
 * examples that exist, so the links appear as the pages do. A slug moves from
 * here to RESUME_EXAMPLES when its page is written.
 */
export const RESUME_EXAMPLE_PLANNED: readonly string[] = []

export function findResumeExample(slug: unknown): ResumeExampleIndexItem | undefined {
  return typeof slug === 'string' ? RESUME_EXAMPLES.find(item => item.slug === slug) : undefined
}

/** The role in the visitor's language, as a heading shows it. */
export function roleName(item: ResumeExampleIndexItem, locale: string): string {
  if (locale === 'cs') return item.roleCs
  if (locale === 'de' && item.roleDe) return item.roleDe
  return item.role
}

/**
 * The role mid-sentence: "a registered nurse resume", "a CNA resume". The
 * Czech and German texts put the role after a colon ("Ukázka životopisu:
 * Učitel", "Lebenslauf-Beispiel: Buchhalter"), so they keep the heading form.
 */
export function rolePhrase(item: ResumeExampleIndexItem, locale = 'en'): string {
  if (locale === 'cs') return item.roleCs
  if (locale === 'de' && item.roleDe) return item.roleDe
  return item.phrase ?? item.role.toLowerCase()
}

/**
 * The role mid-sentence with its article: "a teacher", "an electrician",
 * "an entry-level". By the first letter, which holds for every role listed;
 * an acronym read by its letters ("an MBA") would need its own `phrase`.
 */
export function rolePhraseWithArticle(item: ResumeExampleIndexItem, locale = 'en'): string {
  if (locale === 'cs') return item.roleCs
  if (locale === 'de' && item.roleDe) return item.roleDe
  const phrase = rolePhrase(item)
  return `${/^[aeiou]/i.test(phrase) ? 'an' : 'a'} ${phrase}`
}

/** Content of one page, data/resume-examples/<slug>.json. */
export interface ResumeExampleEntry {
  slug: string
  /** Meta description, unique per page. */
  seoDescription: string
  /** The answer box: what this resume must show at the top, in two or three sentences. */
  short: string
  /**
   * The sample resume, in the same shape the editor saves. The server renders
   * it by slug (server/utils/resumeExamples.ts), never from the request body,
   * and "Use this example" copies it into a new CV minus the made-up identity.
   */
  cv: CVData
  /** Why the sample is written the way it is: three or four short notes. */
  sampleNotes: string[]
  /**
   * Profession: the summary at entry, mid and senior level. Situation: one
   * summary per variant of the situation (high school, college, ...).
   */
  levels: Array<{
    id: string
    label: string
    summary: string
    /** What changes at this level, in a sentence. */
    note: string
  }>
  skills: {
    /** Grouped the way postings group them. */
    hard: Array<{ group: string, items: string[] }>
    soft: string[]
    /** Where the skills go and how to pick them, specific to the role. */
    note: string
  }
  bullets: Array<{ before: string, after: string }>
  /**
   * What the role needs on top of experience: licenses and certifications,
   * clinical hours, clearances. Only credentials checked at the issuer, with a
   * link to it. A situation page uses it for what replaces experience.
   */
  extras: {
    title: string
    intro: string
    items: Array<{ name: string, text: string, issuer?: string, url?: string }>
  }
  /** Why the recommended template (index `template`) fits this role. */
  templateNote: string
  faq: Array<{ question: string, answer: string }>
  /** Slugs from RESUME_EXAMPLES. */
  related: string[]
  /** Extra internal links where they genuinely help, e.g. the first-resume article. */
  links?: Array<{ to: string, label: string }>
}

/** Hard skills listed on a page, across its groups. */
export function hardSkillCount(entry: ResumeExampleEntry): number {
  return entry.skills.hard.reduce((sum, group) => sum + group.items.length, 0)
}

/**
 * The CV a visitor starts from with "Use this example": the sample's wording,
 * without the made-up person. Name, contact details, links and photo are left
 * for the editor to fill from the visitor's own profile.
 */
export function exampleCvSeed(cv: CVData): Partial<CVData> {
  const seed: Partial<CVData> = structuredClone(cv)
  delete seed.id
  delete seed.name
  delete seed.contact
  delete seed.websites
  delete seed.photoUrl
  return seed
}
